import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { ReviewSearchResult } from '../utils/reviewItemSearch';

export type ReviewSearchNavigationContext = 'Reviews' | 'Results';

interface SearchContextType {
  isSearchModalVisible: boolean;
  showSearchModal: (
    reviewId: number,
    navigationContext?: ReviewSearchNavigationContext
  ) => void;
  hideSearchModal: () => void;
  currentReviewId?: number;
  navigationContext: ReviewSearchNavigationContext;
  onItemSelect?: (item: ReviewSearchResult) => void;
  setItemSelectHandler: (handler: (item: ReviewSearchResult) => void) => void;
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
  const [currentReviewId, setCurrentReviewId] = useState<number | undefined>();
  const [navigationContext, setNavigationContext] =
    useState<ReviewSearchNavigationContext>('Reviews');
  const [itemSelectHandler, setItemSelectHandler] =
    useState<((item: ReviewSearchResult) => void) | undefined>();

  const showSearchModal = useCallback((
    reviewId: number,
    nextNavigationContext: ReviewSearchNavigationContext = 'Reviews'
  ) => {
    setCurrentReviewId(reviewId);
    setNavigationContext(nextNavigationContext);
    setIsSearchModalVisible(true);
  }, []);

  const hideSearchModal = useCallback(() => {
    setIsSearchModalVisible(false);
    setCurrentReviewId(undefined);
  }, []);

  const setItemSelectHandlerCallback = useCallback((handler: (item: ReviewSearchResult) => void) => {
    setItemSelectHandler(() => handler);
  }, []);

  const value: SearchContextType = {
    isSearchModalVisible,
    showSearchModal,
    hideSearchModal,
    currentReviewId,
    navigationContext,
    onItemSelect: itemSelectHandler,
    setItemSelectHandler: setItemSelectHandlerCallback,
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = (): SearchContextType => {
  const context = useContext(SearchContext);
  if (context === undefined) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};
